import React, { useState } from 'react';
import {
  useCampaignsQuery,
  useCampaignDetailQuery,
  usePopularAssetsQuery,
} from 'src/api/queries/deliveryAnalyticsQueries';
import { DeliveryAnalyticsFilters } from 'src/types/TDeliveryAnalytics';
import { formatBillingBytes, formatNumberIntl } from 'src/utils/common';
import AssetsTable from '../components/AssetsTable';
import { AsyncSection, DataAsOfBadge, Section } from '../components/common';
import { channelLabel } from '../utils/channelLabels';
import styles from '../styles.module.scss';

// Sentinel the backend uses for the no-campaign bucket; clicking it drills into
// deliveries that carried no campaign key (see analytics.UntaggedCampaign).
const UNTAGGED = '(untagged)';

const fmtDate = (raw: string) => {
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? raw
    : d.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
};

const MiniTable: React.FC<{
  title: string;
  rows: { dimension: string; requests: number }[];
  format?: (dimension: string) => string;
}> = ({ title, rows, format }) => (
  <div className={styles.miniTable}>
    <h4>{title}</h4>
    {rows.length === 0 ? (
      <p className={styles.miniEmpty}>—</p>
    ) : (
      <ul>
        {rows.slice(0, 5).map((r) => (
          <li key={r.dimension || '(empty)'}>
            <span>{format ? format(r.dimension) : r.dimension || '(unknown)'}</span>
            <span>{formatNumberIntl(r.requests)}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const CampaignDetail: React.FC<{
  campaignKey: string;
  campaignName?: string;
  filters: DeliveryAnalyticsFilters;
  onClose: () => void;
}> = ({ campaignKey, campaignName, filters, onClose }) => {
  const { data, isLoading, error } = useCampaignDetailQuery(campaignKey, filters);
  const [viewAll, setViewAll] = useState(false);

  // Lazy full-asset list for this campaign, only fetched when "View all" is on.
  // Reuses the popular-assets endpoint scoped by campaign_key (no 10-asset cap;
  // limit is the backend's 1000 ceiling).
  const all = usePopularAssetsQuery(
    { ...filters, campaignKey, metric: 'requests', limit: 1000 },
    { enabled: viewAll },
  );

  const topAssets = data?.top_assets ?? [];
  const distinctAssets = data?.totals.distinct_assets ?? 0;
  const canViewAll = distinctAssets > topAssets.length;
  const assetRows = viewAll ? all.data?.data ?? [] : topAssets;
  const isUntagged = campaignKey === UNTAGGED;
  const title = campaignName || campaignKey;

  return (
    <Section
      title={isUntagged ? 'Untagged traffic (no campaign)' : `Campaign: ${title}`}
      headerRight={
        <button type="button" className={styles.clearButton} onClick={onClose}>
          Close
        </button>
      }
    >
      <AsyncSection isLoading={isLoading} error={error} isEmpty={!data}>
        {data && (
          <>
            <div className={styles.statGrid}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{formatNumberIntl(data.totals.requests)}</div>
                <div className={styles.statLabel}>Requests</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{formatBillingBytes(data.totals.bytes)}</div>
                <div className={styles.statLabel}>Bytes</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>
                  {formatNumberIntl(data.totals.distinct_assets)}
                </div>
                <div className={styles.statLabel}>Distinct assets</div>
              </div>
            </div>

            <div className={styles.assetsBlock}>
              <div className={styles.assetsHeader}>
                <h4>{viewAll ? 'All assets' : 'Top assets'}</h4>
                {canViewAll && (
                  <button
                    type="button"
                    className={styles.clearButton}
                    onClick={() => setViewAll((v) => !v)}
                  >
                    {viewAll
                      ? `Show top ${topAssets.length}`
                      : `View all ${formatNumberIntl(distinctAssets)} assets`}
                  </button>
                )}
              </div>
              <AsyncSection
                isLoading={viewAll && all.isLoading}
                error={viewAll ? all.error : null}
                isEmpty={assetRows.length === 0}
                emptyLabel={
                  isUntagged
                    ? 'No untagged deliveries in the selected range.'
                    : 'No assets for this campaign in the selected range.'
                }
              >
                <AssetsTable rows={assetRows} showActions />
              </AsyncSection>
            </div>

            <div className={styles.miniTableGrid}>
              <MiniTable title="By channel" rows={data.channel_breakdown} format={channelLabel} />
              <MiniTable title="By country" rows={data.audience_by_country} />
              <MiniTable title="By device" rows={data.audience_by_device} />
              <MiniTable title="Top transforms" rows={data.top_transforms} />
              <MiniTable title="By placement" rows={data.placement_breakdown} />
              <MiniTable title="By variant" rows={data.variant_breakdown} />
            </div>
          </>
        )}
      </AsyncSection>
    </Section>
  );
};

const CampaignsTab: React.FC<{ filters: DeliveryAnalyticsFilters }> = ({ filters }) => {
  const [selected, setSelected] = useState<{ key: string; name: string } | null>(null);
  const { data, isLoading, error } = useCampaignsQuery({ ...filters, limit: 100 });
  const rows = data?.data ?? [];

  return (
    <>
      <Section
        title="Campaigns"
        description="Campaigns by delivery volume, including untagged traffic. Select one to drill in."
        headerRight={<DataAsOfBadge value={data?.data_as_of} loading={isLoading} />}
      >
        <AsyncSection isLoading={isLoading} error={error} isEmpty={rows.length === 0}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Campaign</th>
                <th className={styles.numCol}>Assets</th>
                <th className={styles.numCol}>Requests</th>
                <th className={styles.numCol}>Bytes</th>
                <th>First seen</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const name = r.campaign_name || r.campaign_key;
                return (
                  <tr
                    key={r.campaign_key}
                    className={styles.clickableRow}
                    onClick={() => setSelected({ key: r.campaign_key, name })}
                  >
                    <td>{name}</td>
                    <td className={styles.numCol}>{formatNumberIntl(r.distinct_assets)}</td>
                    <td className={styles.numCol}>{formatNumberIntl(r.requests)}</td>
                    <td className={styles.numCol}>{formatBillingBytes(r.bytes)}</td>
                    <td>{fmtDate(r.first_seen)}</td>
                    <td>{fmtDate(r.last_seen)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </AsyncSection>
      </Section>

      {selected && (
        <CampaignDetail
          campaignKey={selected.key}
          campaignName={selected.name}
          filters={filters}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
};

export default CampaignsTab;
